import { Resource as ResourceType } from '../types';

// "Contrat (PDF)" of the "Contrat" tab, on the order's contract
// (`record.contract`, updated through /contracts): shown in two fields, the
// PDF to sign then its e-signature (see withPdfDropzone's `display`)
const contractPdfDropzoneParams = {
  attachmentKind: 'contract',
  entity: { path: 'contract', resource: 'contracts' },
  // "Créer le PDF à signer" from the generated contract (Google Doc), on the
  // purchase order
  recordResource: 'purchase_orders',
  generatePdfPath: 'generate_contract_pdf',
  // "Contrat (PDF)" older than the Google Doc, asked by the field only
  pdfStatusPath: 'contract_pdf_status',
  messages: {
    noEntity: 'contracts.noResults',
    dropzoneLabel: 'resource.contracts.pdf.dropzone.label',
    uploading: 'resource.contracts.pdf.uploading.label',
    invalidFormat: 'resource.contracts.dropzone.error.invalid_format',
    fileTooLarge: 'resource.contracts.dropzone.error.file_too_large',
    replaced: 'resource.contracts.pdf.notify.replaced',
    replaceFailed: 'resource.contracts.pdf.notify.replace_failed',
    confirmExpertSigned: 'resource.contracts.pdf.confirm_expert_signed.label',
    confirmSignatureLaunched:
      'resource.contracts.pdf.confirm_signature_launched.label',
    launchLabel: 'resource.contracts.pdf.launch.label',
    launchSucceeded: 'resource.contracts.pdf.launch.succeeded',
    resendLabel: 'resource.contracts.pdf.resend.label',
    resendSucceeded: 'resource.contracts.pdf.resend.succeeded',
    signLabel: 'resource.contracts.pdf.sign.label',
    signFailed: 'resource.contracts.pdf.sign.failed',
    awaitingExpert: 'contract.esignature.pending',
    awaitingAdmin: 'resource.contracts.pdf.sign.awaiting_admin',
    phoneModalTitle: 'esignature.modal.contract.title',
    phoneModalDescription: 'esignature.modal.contract.subtitle',
    pdfOutdated: 'resource.purchase_orders.notify.contract_pdf_outdated',
    generatePdfLabel:
      'resource.purchase_orders.action_buttons.generate_contract_pdf.label',
    generatePdfSucceeded:
      'resource.purchase_orders.notify.contract_pdf_generated',
    generatePdfUnavailable:
      'resource.purchase_orders.notify.contract_pdf_requires_contract',
  },
};

export const purchaseOrder: ResourceType = {
  name: 'purchase_orders',
  label: 'resource.purchase_orders.label',
  hideResource: false,
  attributes: [
    {
      key: 'id',
      label: 'resource.purchase_orders.field.id.label',
      type: 'text',
    },
    {
      key: 'order_number',
      label: 'resource.purchase_orders.field.order_number.label',
      type: 'text',
    },
    {
      key: 'date',
      label: 'resource.purchase_orders.field.date.label',
      type: 'date',
    },
    {
      key: 'status',
      label: 'resource.purchase_orders.field.status.label',
      type: 'select',
      choices: [
        {
          id: 'pending',
          name: 'resource.purchase_orders.field.status.choices.pending.label',
        },
        {
          id: 'incomplete',
          name: 'resource.purchase_orders.field.status.choices.incomplete.label',
        },
        {
          id: 'success',
          name: 'resource.purchase_orders.field.status.choices.success.label',
        },
        {
          id: 'error',
          name: 'resource.purchase_orders.field.status.choices.error.label',
        },
      ],
    },
    {
      key: 'created_at',
      label: 'resource.purchase_orders.field.created_at.label',
      type: 'date',
    },
    {
      key: 'updated_at',
      label: 'resource.purchase_orders.field.updated_at.label',
      type: 'date',
    },
    {
      key: 'error',
      label: 'resource.purchase_orders.field.error.label',
      type: 'text',
    },
    {
      key: 'synchronized_at',
      label: 'resource.purchase_orders.field.synchronized_at.label',
      type: 'date',
    },
    {
      key: 'attachment',
      label: 'resource.purchase_orders.field.attachment.label',
      type: 'buttons',
    },
    {
      key: 'client_id',
      label: 'resource.purchase_orders.field.client_id.label',
      type: 'reference',
      reference: 'users',
    },
    {
      key: 'mission_id',
      label: 'resource.purchase_orders.field.mission_id.label',
      type: 'reference',
      reference: 'missions',
    },
    {
      key: 'expert_id',
      label: 'resource.purchase_orders.field.expert_id.label',
      type: 'reference',
      reference: 'users',
    },
    {
      key: 'vat',
      label: 'resource.purchase_orders.field.vat.label',
      type: 'boolean',
    },
    {
      key: 'payment_terms',
      label: 'resource.purchase_orders.field.payment_terms.label',
      type: 'select',
      choices: [
        {
          id: 'payment_client',
          name: 'resource.purchase_orders.field.payment_terms.choices.payment_client.label',
        },
        {
          id: 'ten_days',
          name: 'resource.purchase_orders.field.payment_terms.choices.ten_days.label',
        },
      ],
    },
    {
      // Choices: the record's available_contract_templates (see the
      // choicesFromRecord extension on its input)
      key: 'contract_template',
      label: 'resource.purchase_orders.field.contract_template.label',
      type: 'select',
      choices: [],
    },
    {
      key: 'special_terms',
      label: 'resource.purchase_orders.field.special_terms.label',
      type: 'wysiwyg',
    },
    {
      // Google Doc generated by "Générer le contrat", in the contracts
      // folder of Google Drive
      key: 'google_doc_url',
      label: 'resource.purchase_orders.field.contract_draft.label',
      type: 'url',
    },
    {
      // Same Google Doc, downloaded as Word (to insert the table by hand)
      key: 'google_doc_docx_url',
      label: 'resource.purchase_orders.field.contract_docx.label',
      type: 'url',
    },
    {
      // "Type de signature" of the order's contract, see the "Contrat" tab
      key: 'contract_signature_mode',
      label: 'resource.purchase_orders.field.contract_signature_mode.label',
      type: 'radio',
      choices: [
        { id: 'manual', name: 'resource.contracts.signature_mode.manual' },
        {
          id: 'automatic',
          name: 'resource.contracts.signature_mode.automatic',
        },
      ],
    },
    {
      // PDF of the contract to sign (on the order's contract), see the
      // "Contrat" tab
      key: 'contract_pdf',
      label: 'resource.contract.section.pdf.title',
      type: 'buttons',
    },
    {
      // E-signature of the contract's PDF, see the "Contrat" tab
      key: 'contract_signature',
      label: 'resource.purchase_orders.section.signature.title',
      type: 'buttons',
    },
    {
      key: 'contract_pdf_signed',
      label: 'resource.contracts.field.contract_pdf_signed.label',
      type: 'buttons',
    },
    {
      key: 'expert_manual_active',
      type: 'boolean',
    },
    {
      key: 'expert_attributes',
      label:
        'resource.purchase_orders.field.expert_attributes.first_name.label',
      type: 'object',
      shape: [
        {
          key: 'first_name',
          label:
            'resource.purchase_orders.field.expert_attributes.first_name.label',
          type: 'text',
        },
        {
          key: 'last_name',
          label:
            'resource.purchase_orders.field.expert_attributes.last_name.label',
          type: 'text',
        },
        {
          key: 'email',
          label: 'resource.purchase_orders.field.expert_attributes.email.label',
          type: 'email',
        },
      ],
    },
  ],
  pages: {
    list: {
      hasEditButton: true,
      extensions: [{ key: 'purchaseOrdersDropzone' }],
      search: {
        enabled: true,
        label: 'filter.search.label',
      },
      fields: [
        { source: 'order_number' },
        { source: 'date' },
        {
          source: 'client_id',
          label: 'resource.purchase_orders.field.client_id.label',
          child: { source: 'users.full_name' },
        },
        {
          source: 'mission_id',
          label: 'resource.purchase_orders.field.mission_id.label',
          child: { source: 'missions.context' },
        },
        { source: 'status' },
      ],
      filters: [
        {
          source: 'status',
          type: 'select',
          label: 'resource.purchase_orders.field.status.label',
          choices: [
            {
              id: 'pending',
              name: 'resource.purchase_orders.field.status.choices.pending.label',
            },
            {
              id: 'incomplete',
              name: 'resource.purchase_orders.field.status.choices.incomplete.label',
            },
            {
              id: 'success',
              name: 'resource.purchase_orders.field.status.choices.success.label',
            },
            {
              id: 'error',
              name: 'resource.purchase_orders.field.status.choices.error.label',
            },
          ],
        },
        {
          source: 'date',
          type: 'dateRange',
          label: 'resource.purchase_orders.field.date.label',
        },
      ],
      bulkActionButtons: [
        {
          name: 'synchronize',
          type: 'update',
          label:
            'resource.purchase_orders.bulk_action_buttons.synchronize.label',
          extraUrl: '/synchronize',
          values: {},
          successMessage: 'resource.purchase_orders.notify.synchronize.started',
          refreshWhenDone: {
            property: 'status',
            whileIn: ['pending'],
            doneMessage: 'resource.purchase_orders.notify.synchronize.done',
          },
        },
      ],
    },
    edit: {
      // Stay on this record's edit page after saving instead of react-admin's
      // default redirect to the index/list page.
      singleRedirect: {
        page: 'edit',
        basePath: '/purchase_orders',
        recordId: 'id',
      },
      actions: [
        {
          name: 'synchronize',
          type: 'update',
          label: 'resource.purchase_orders.action_buttons.synchronize.label',
          resourceId: 'id',
          // The contract signed, the order no longer changes
          disabled: [
            { property: 'contract.fully_signed', op: 'eq', value: true },
          ],
          extraUrl: '/synchronize',
          values: {},
          successMessage: 'resource.purchase_orders.notify.synchronize.started',
          refreshWhenDone: {
            property: 'status',
            whileIn: ['pending'],
            doneMessage: 'resource.purchase_orders.notify.synchronize.done',
          },
        },
        {
          // Fills the chosen template (for now its "Modalités particulières")
          name: 'generate_contract',
          type: 'update',
          label:
            'resource.purchase_orders.action_buttons.generate_contract.label',
          resourceId: 'id',
          extraUrl: '/generate_contract',
          values: {},
          // The contract signed is the final one
          disabled: [
            { property: 'contract.fully_signed', op: 'eq', value: true },
          ],
          // Generates from the saved template / special terms
          checkUnsavedChanges: true,
          // The signature in progress is then canceled by the API, to be
          // launched again on the new contract
          conditionalConfirmations: [
            {
              rules: [
                {
                  property: 'contract.expert_signed_awaiting_admin',
                  op: 'eq',
                  value: true,
                },
              ],
              content:
                'resource.purchase_orders.confirm.regenerate_contract_expert_signed',
              values: { expert_name: 'contract.expert_full_name' },
            },
            {
              rules: [
                {
                  property: 'contract.signature_launched',
                  op: 'eq',
                  value: true,
                },
                { property: 'contract.fully_signed', op: 'neq', value: true },
              ],
              content:
                'resource.contracts.pdf.confirm_signature_launched.label',
              values: { expert_name: 'contract.expert_full_name' },
            },
          ],
          successMessage: 'resource.purchase_orders.notify.contract_generated',
        },
      ],
      // "Modalités particulières – propagation": apply to this consultant
      // only, or to the whole company (see PurchaseOrderSpecialTermsPropagationService)
      // "Alerte en cas de modification après génération": special terms
      // changed since the contract was generated, on this order or by
      // propagation from another order of the company
      recordWarning: {
        ifTrue: 'contract_outdated?',
        message: 'resource.purchase_orders.notify.regenerate_contract',
      },
      saveScope: {
        field: 'special_terms',
        param: 'special_terms_scope',
        title: 'resource.purchase_orders.special_terms_scope.title',
        content: 'resource.purchase_orders.special_terms_scope.content',
        options: [
          {
            value: 'consultant',
            label: 'resource.purchase_orders.special_terms_scope.consultant',
          },
          {
            value: 'company',
            label: 'resource.purchase_orders.special_terms_scope.company',
          },
        ],
      },
      left: {
        tabs: [
          {
            key: 'information',
            title: 'resource.purchase_orders.tab.information.title',
            sections: [
              {
                key: 'information',
                title: 'resource.purchase_orders.section.information.title',
                layout: [
                  ['order_number', 'date'],
                  ['status', 'synchronized_at'],
                  ['error'],
                  ['expert_id'],
                  ['expert_manual_active'],
                  ['expert_attributes.first_name'],
                  ['expert_attributes.last_name'],
                  ['expert_attributes.email'],
                  ['vat', 'payment_terms'],
                ],
                inputs: {
                  order_number: { width: 6 },
                  date: { width: 6 },
                  status: { width: 6, disabled: true },
                  synchronized_at: { disabled: true, width: 6 },
                  error: { disabled: true },
                  expert_id: {
                    type: 'reference',
                    reference: 'users',
                    filter: { kind: 'expert' },
                    label: 'resource.purchase_orders.field.expert_id.label',
                    child: {
                      type: 'autocomplete',
                      optionText: 'full_name',
                      extensions: [{ key: 'manualExpertChoice' }],
                      options: {
                        perPage: 50,
                        allowEmpty: true,
                        resettable: true,
                        clearAlwaysVisible: true,
                      },
                    },
                  },
                  vat: {
                    width: 6,
                    validators: [{ key: 'required' }],
                  },
                  payment_terms: {
                    width: 6,
                    validators: [{ key: 'required' }],
                  },
                  expert_manual_active: {
                    initialValue: false,
                    type: 'boolean',
                    rules: {
                      hide: [
                        {
                          property: 'expert_manual_active',
                          op: 'neq',
                          value: 'force_hide',
                        },
                      ],
                    },
                  },
                  'expert_attributes.first_name': {
                    validators: [
                      {
                        key: 'required',
                        rules: [
                          {
                            property: 'expert_manual_active',
                            op: 'eq',
                            value: true,
                          },
                        ],
                      },
                    ],
                    rules: {
                      hide: [
                        {
                          property: 'expert_manual_active',
                          op: 'neq',
                          value: true,
                        },
                      ],
                    },
                  },
                  'expert_attributes.last_name': {
                    validators: [
                      {
                        key: 'required',
                        rules: [
                          {
                            property: 'expert_manual_active',
                            op: 'eq',
                            value: true,
                          },
                        ],
                      },
                    ],
                    rules: {
                      hide: [
                        {
                          property: 'expert_manual_active',
                          op: 'neq',
                          value: true,
                        },
                      ],
                    },
                  },
                  'expert_attributes.email': {
                    type: 'email',
                    validators: [
                      {
                        key: 'required',
                        rules: [
                          {
                            property: 'expert_manual_active',
                            op: 'eq',
                            value: true,
                          },
                        ],
                      },
                    ],
                    rules: {
                      hide: [
                        {
                          property: 'expert_manual_active',
                          op: 'neq',
                          value: true,
                        },
                      ],
                    },
                  },
                },
                fields: {},
              },
              {
                key: 'pdf',
                title: 'resource.purchase_orders.section.pdf.title',
                layout: [['attachment']],
                inputs: {},
                fields: {
                  attachment: {
                    label: 'resource.purchase_orders.field.attachment.label',
                    addLabel: true,
                    extensions: [{ key: 'purchaseOrderPdf' }],
                  },
                },
              },
            ],
          },
          {
            // Contract template choice and generation (KICKLOX-9119), then
            // the PDF to sign and its e-signature, on the order's contract
            // (`record.contract`, updated through /contracts)
            key: 'contract',
            title: 'contract.title',
            sections: [
              {
                key: 'contract_template',
                title: 'resource.purchase_orders.field.contract_template.label',
                layout: [['contract_template']],
                inputs: {
                  // Preselected by the API (CGI or other clients template)
                  contract_template: {
                    validators: [{ key: 'required' }],
                    extensions: [
                      {
                        key: 'choicesFromRecord',
                        params: { source: 'available_contract_templates' },
                      },
                    ],
                  },
                },
                fields: {},
              },
              {
                key: 'contract_generated',
                title: 'resource.purchase_orders.field.contract_draft.label',
                layout: [['google_doc_url'], ['google_doc_docx_url']],
                inputs: {},
                fields: {
                  google_doc_url: {
                    addLabel: true,
                    linkLabel:
                      'resource.purchase_orders.field.google_doc_url.link',
                  },
                  google_doc_docx_url: {
                    addLabel: true,
                    linkLabel:
                      'resource.purchase_orders.field.google_doc_docx_url.link',
                  },
                },
              },
              {
                key: 'contract_pdf',
                title: 'resource.contract.section.pdf.title',
                // Below the generated contract's links
                spaced: true,
                // The PDF to sign, then its e-signature: "Type de signature"
                // above the launch button
                layout: [
                  ['contract_pdf'],
                  ['contract_signature_mode'],
                  ['contract_signature'],
                  ['contract_pdf_signed'],
                ],
                inputs: {
                  // Above the launch button; "Manuelle" by default (the
                  // API's Contract#signature_mode)
                  contract_signature_mode: {
                    extensions: [{ key: 'contractSignatureMode' }],
                  },
                },
                fields: {
                  contract_pdf: {
                    // Replaced by "Contrat signé" below once signed (only
                    // hidden: the signed PDF is reached through it)
                    rules: {
                      hide: [
                        {
                          property: 'contract.fully_signed',
                          op: 'eq',
                          value: true,
                        },
                      ],
                    },
                    addLabel: true,
                    label:
                      'resource.contracts.field.contract_pdf_to_sign.label',
                    extensions: [
                      {
                        key: 'pdfDropzone',
                        params: {
                          ...contractPdfDropzoneParams,
                          display: 'document',
                        },
                      },
                    ],
                  },
                  contract_signature: {
                    extensions: [
                      {
                        key: 'pdfDropzone',
                        params: {
                          ...contractPdfDropzoneParams,
                          display: 'signature',
                        },
                      },
                    ],
                  },
                  contract_pdf_signed: {
                    addLabel: true,
                    extensions: [
                      {
                        key: 'attachmentsFiles',
                        // As the contract's PDF above (pdfDropzone)
                        params: { icon: true },
                      },
                    ],
                  },
                },
              },
            ],
          },
          {
            key: 'special_terms',
            title: 'resource.purchase_orders.tab.special_terms.title',
            sections: [
              {
                key: 'special_terms',
                title: 'resource.purchase_orders.section.special_terms.title',
                // Optional: left empty, the contract's "Modalités
                // particulières" page stays blank
                layout: [['special_terms']],
                inputs: {
                  special_terms: {},
                },
                fields: {},
              },
            ],
          },
        ],
      },
      right: [
        {
          sections: [
            {
              key: 'details',
              title: 'resource.purchase_orders.section.details.title',
              layout: [['id'], ['created_at', 'updated_at']],
              inputs: {
                id: { disabled: true },
                created_at: { disabled: true, width: 6 },
                updated_at: { disabled: true, width: 6 },
              },
              fields: {},
            },
          ],
        },
        {
          sections: [
            {
              key: 'links',
              title: 'resource.purchase_orders.section.links.title',
              layout: [['client_id'], ['mission_id'], ['expert_id']],
              inputs: {},
              fields: {
                client_id: {
                  addLabel: true,
                  child: { type: 'text', source: 'users.full_name' },
                },
                mission_id: {
                  addLabel: true,
                  child: { type: 'text', source: 'missions.context' },
                },
                expert_id: {
                  addLabel: true,
                  child: { type: 'text', source: 'users.full_name' },
                },
              },
            },
          ],
        },
        {
          sections: [
            {
              key: 'history',
              title: 'resource.purchase_orders.section.history.title',
              layout: [],
              inputs: {},
              history: true,
              filterResource: 'purchase_orders',
              filterAttribute: 'id',
            },
          ],
        },
      ],
    },
  },
};
